import { Text, Modal } from "@mantine/core";
import {
  InputGroup,
  ForgotPasswordInput,
  color,
} from "@funded-here-interface/common";
import { ChangeEvent, FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setEnvironment,
  setToken,
  setRegtankOnboardingFlag,
} from "@funded-here-interface/common/src/features/auth/authSlice_Admin";
import { useStyles } from "./LoginCss";
import { toast } from "react-toastify";
import useLoginMutation from "@funded-here-interface/common/src/hooks/useLogin";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUsers";
import Flogo from "../../assets/Funded_Here_LogoA4.svg";
import useVerifyTwoFAToken from "@funded-here-interface/common/src/hooks/useVerifyTwoFAToken";
import { useDisclosure } from "@mantine/hooks";
import { LargeBtn } from "@funded-here-interface/common";
import OtpField from "@funded-here-interface/common/src/components/OtpField/OtpField";

const Login: FC = () => {
  const { classes } = useStyles();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState({ email_error: "", pass_error: "" });
  const navigate = useNavigate();
  const mutation = useLoginMutation();
  const getUserMutation = useGetUser();
  const dispatch = useDispatch();
  const verifyTwoFATokenMutation = useVerifyTwoFAToken();
  const [openedVerifyOTP, modalAction] = useDisclosure(false);
  const [otp, setOtp] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newError = { email_error: "", pass_error: "" };
    if (!email) {
      newError.email_error = "Email is required";
    }
    if (!password) {
      newError.pass_error = "Password is required";
    }

    setError(newError);

    if (email && password) {
      mutation.mutate(
        { email, password },
        {
          onSuccess: async (data) => {
            if (data) {
              if (data.stage === "setup2FA") {
                const dataForQRCode = {
                  email: email.toLowerCase(),
                  role: "Admin",
                };
                navigate("/two-auth-QR-code", { state: dataForQRCode });
              } else if (data.stage === "verifyOTP") {
                modalAction.open();
              } else {
                sessionStorage.setItem("Auth", data);
                const token = data;
                dispatch(setToken({ token }));
                getUserMutation.mutate(
                  {
                    token,
                  },
                  {
                    onSuccess: (userData) => {
                      const data = userData.user;
                      const role = data?.orgRole?.[0]?.role?.name;
                      const roleId = data?.currentOrgRoleId;

                      sessionStorage.setItem("orgRoleId", roleId);

                      dispatch(setEnvironment({ env: userData.environment }));
                      dispatch(
                        setRegtankOnboardingFlag({
                          regtankOnboardingFeatureFlag:
                            data.regtankOnboardingFeatureFlag,
                        })
                      );
                      if (role === "product-manager") {
                        navigate("/pm-dashboard/activenotes");
                      } else if (role === "operation-and-risk") {
                        navigate("/org-list");
                      } else if (role === "sales-support") {
                        navigate("/sales-support/new");
                      } else {
                        toast.error("Credentials are not valid.");
                      }
                    },
                    onError: (e) => {
                      toast.error((e as Error).message);
                    },
                  }
                );
              }
            }
          },
          onError: (e) => {
            toast.error((e as Error).message);
          },
        }
      );
    }
  };

  const onChange = (value: string) => {
    setOtp(value);
  };

  const handleVerifyOtp = async () => {
    if (otp) {
      verifyTwoFATokenMutation.mutate(
        { token: otp, email: email.toLowerCase() },
        {
          onSuccess: (data) => {
            if (data) {
              sessionStorage.setItem("Auth", data);
              const token = data;
              dispatch(setToken({ token }));
              getUserMutation.mutate(
                {
                  token,
                },
                {
                  onSuccess: (userData) => {
                    const data = userData.user;
                    const role = data?.orgRole?.[0]?.role?.name;
                    const roleId = data?.currentOrgRoleId;

                    sessionStorage.setItem("orgRoleId", roleId);

                    dispatch(setEnvironment({ env: userData.environment }));
                    dispatch(
                      setRegtankOnboardingFlag({
                        regtankOnboardingFeatureFlag:
                          data.regtankOnboardingFeatureFlag,
                      })
                    );
                    if (role === "product-manager") {
                      navigate("/pm-dashboard/activenotes");
                    } else if (role === "operation-and-risk") {
                      navigate("/org-list");
                    } else if (role === "sales-support") {
                      navigate("/sales-support/new");
                    } else {
                      toast.error("Credentials are not valid.");
                    }
                  },
                  onError: (e) => {
                    toast.error((e as Error).message);
                  },
                }
              );
              modalAction.close();
            }
          },
          onError: (e) => {
            if (
              (e as Error).message ===
              "User is not verified for two factor authentication"
            ) {
              const otpError: HTMLElement | null =
                document.getElementById("otpError");
              if (otpError) {
                otpError.innerHTML =
                  "<span style='color: red;'>" +
                  "The code you entered is incorrect. Please try again.</span>";
              }
            } else {
              toast.error((e as Error).message);
            }
          },
        }
      );
    }
  };

  return (
    <div className={classes.container}>
      <div>
        <img className={classes.logo} src={Flogo} alt="fh_logo" />
      </div>
      <div style={{ textAlign: "left" }}>
        <Text className={classes.loginHeading}>
          Welcome to the FundedHere <br /> Admin Portal
        </Text>
      </div>
      <div className={classes.loginContainer}>
        <form className={classes.inputGroupContainer} onSubmit={handleSubmit}>
          <div style={{ marginLeft: "0px" }}>
            <InputGroup
              labelText="Email"
              id="your-email"
              value={email}
              placeHolder="Enter Email Address"
              onChange={(value: string) => setEmail(value)}
              isErrored={error.email_error}
            />
          </div>

          <div style={{ marginLeft: "0px" }}>
            <ForgotPasswordInput
              className="LoginForgotPasswordInput"
              name="password"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setPassword(e.target.value.trim())
              }
              forgotPassword={() => navigate("/password-reset")}
              isErrored={error.pass_error}
            />
          </div>

          <div className={classes.buttonDiv}>
            <button
              className={classes.input}
              type="submit"
              style={{ width: "100%", fontSize: "17px", fontWeight: 700 }}
            >
              Login
            </button>
          </div>
        </form>
      </div>
      <Modal
        classNames={{
          header: classes.modalHeader,
          title: classes.modalTitle,
        }}
        opened={openedVerifyOTP}
        onClose={modalAction.close}
        title="Two-Factor Authentication"
        color="gray"
        centered
        overlayProps={{
          blur: 2,
          opacity: 0.1,
        }}
      >
        <OtpField length={6} onChange={onChange} onEnter={handleVerifyOtp} />
        <div
          style={{
            textAlign: "center",
            fontSize: "10px",
            marginBottom: "20px",
          }}
        >
          <span id="otpError"></span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "5px",
            marginBottom: "50px",
          }}
        >
          <LargeBtn
            onClick={handleVerifyOtp}
            backgroundColor={color.FHGREEN}
            textColor="#FFFFFF"
            width="5%"
            disabled={otp !== "" ? false : true}
          >
            Verify
          </LargeBtn>
        </div>
      </Modal>
    </div>
  );
};

export default Login;
